import Markdown from "react-markdown";

import { cn } from "@/lib/utils";

import { Link } from "./link";

interface MarkdownTextProps {
  /** The markdown content to render */
  children: string;
  /** Additional CSS classes */
  className?: string;
  /** Whether to render inline (no block elements like <p>) */
  inline?: boolean;
}

/**
 * MarkdownText Component
 *
 * Renders plain markdown strings as styled HTML.
 * Used as an alternative to PortableText for simpler rich text content.
 *
 * Supports:
 * - Bold text: **text** or __text__
 * - Italic text: *text* or _text_
 * - Links: [text](url)
 * - Inline code: `code`
 *
 * @example
 * // Simple text with bold
 * <MarkdownText>**Bold** and normal text</MarkdownText>
 *
 * @example
 * // Text with links
 * <MarkdownText>Visit [our site](https://wayflyer.com) for more info</MarkdownText>
 *
 * @example
 * // Inline rendering (no wrapping <p> tags)
 * <MarkdownText inline>**Bold text** inline</MarkdownText>
 */
export function MarkdownText({
  children,
  className,
  inline = false,
}: MarkdownTextProps) {
  // Don't render if empty
  if (!children || children.trim() === "") {
    return null;
  }

  const content = (
    <Markdown
      components={{
        // Custom link component that handles internal/external links
        a: ({ href, children }) => {
          const isExternal =
            href?.startsWith("http") || href?.startsWith("mailto:");
          return (
            <Link
              href={href ?? "#"}
              external={isExternal}
              className="underline hover:no-underline"
            >
              {children}
            </Link>
          );
        },
        // For inline mode, don't wrap in paragraph
        p: ({ children }) => {
          if (inline) {
            return <>{children}</>;
          }
          return <p>{children}</p>;
        },
        // Style bold text
        strong: ({ children }) => (
          <strong className="font-semibold">{children}</strong>
        ),
        // Style italic text
        em: ({ children }) => <em className="italic">{children}</em>,
        // Style inline code
        code: ({ children }) => (
          <code className="bg-grey-100 rounded px-1 py-0.5 font-mono text-sm">
            {children}
          </code>
        ),
      }}
    >
      {children}
    </Markdown>
  );

  if (inline) {
    return className ? <span className={className}>{content}</span> : content;
  }

  return <div className={cn(className)}>{content}</div>;
}
